import React, { useContext, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { SessionContext } from '../../../providers/session/SessionContext'
import { ElementWithTooltip } from '../../common/element-with/element-with-tooltip/ElementWithTooltip'
import { refreshListView } from '../../common/gusl-table/queryParamsSlice'
import { PnlStatusStyled } from './styled'

const ACTIVE_POLL_MS = 2_000
const CURRENT_POLL_MS = 5_000

type PnlStatus = 'CURRENT' | 'STALE' | 'PROCESSING' | 'RETRYING'

interface PnlProjectionStatusResponse {
    status: PnlStatus
    invalidFromDate?: string
    revision: number
    nextAttemptAt?: string
    statusChangedAt?: string
}

interface PnlProjectionStatusProps {
    bettorId: string
    tableCode: string
}

const displayByStatus = {
    CURRENT: { icon: 'fa-solid fa-circle-check', label: 'P&L current' },
    STALE: { icon: 'fa-solid fa-clock', label: 'P&L update queued' },
    PROCESSING: { icon: 'fa-solid fa-spinner fa-spin', label: 'Updating P&L' },
    RETRYING: { icon: 'fa-solid fa-triangle-exclamation', label: 'P&L update delayed' },
}

const formatTime = (value: string): string =>
    new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(new Date(value))

const getTooltip = (projection: PnlProjectionStatusResponse): string => {
    const affectedDate = projection.invalidFromDate ? `Recalculating from ${projection.invalidFromDate}` : ''
    const nextAttempt = projection.nextAttemptAt ? formatTime(projection.nextAttemptAt) : ''
    const statusChanged = projection.statusChangedAt ? formatTime(projection.statusChangedAt) : ''

    const statusDetail = {
        CURRENT: statusChanged ? `Last automatic update completed ${statusChanged}` : 'No recalculation pending',
        STALE: nextAttempt ? `Scheduled for ${nextAttempt}` : 'Waiting to be scheduled',
        PROCESSING: statusChanged ? `Started ${statusChanged}` : 'Recalculation in progress',
        RETRYING: nextAttempt ? `Retry scheduled for ${nextAttempt}` : 'Waiting to retry',
    }[projection.status]

    return [statusDetail, affectedDate].filter(Boolean).join('. ')
}

export const PnlProjectionStatus = ({ bettorId, tableCode }: PnlProjectionStatusProps): React.ReactElement => {
    const sessionContext = useContext(SessionContext)
    const dispatch = useAppDispatch()
    const previousStatus = useRef<PnlStatus>()
    const previousRevision = useRef<number>()
    const [projection, setProjection] = useState<PnlProjectionStatusResponse>()
    const [unavailable, setUnavailable] = useState(false)

    useEffect(() => {
        let active = true
        let timer: number | undefined
        let controller: AbortController | undefined

        previousStatus.current = undefined
        previousRevision.current = undefined

        const loadStatus = async () => {
            controller = new AbortController()
            try {
                const response = await sessionContext.get<PnlProjectionStatusResponse>(
                    `/accounts/${encodeURIComponent(bettorId)}/pnl-status`,
                    controller
                )
                if (!active) return

                const next = response.data
                const projectionChanged =
                    next.status === 'CURRENT' &&
                    previousStatus.current !== undefined &&
                    (previousStatus.current !== 'CURRENT' || previousRevision.current !== next.revision)
                previousStatus.current = next.status
                previousRevision.current = next.revision
                setProjection(next)
                setUnavailable(false)

                if (projectionChanged) {
                    dispatch(refreshListView({ code: tableCode }))
                }

                timer = window.setTimeout(loadStatus, next.status === 'CURRENT' ? CURRENT_POLL_MS : ACTIVE_POLL_MS)
            } catch (error) {
                if (!active || controller.signal.aborted) return
                setUnavailable(true)
                timer = window.setTimeout(loadStatus, CURRENT_POLL_MS)
            }
        }

        loadStatus()
        return () => {
            active = false
            controller?.abort()
            if (timer) window.clearTimeout(timer)
        }
    }, [bettorId, dispatch, sessionContext, tableCode])

    if (unavailable) {
        return <PnlStatusStyled status="UNAVAILABLE">P&L status unavailable</PnlStatusStyled>
    }
    if (!projection) return <></>

    const display = displayByStatus[projection.status]
    const tooltip = getTooltip(projection)
    return (
        <ElementWithTooltip
            placement="bottom"
            tooltip={<span>{tooltip}</span>}
            element={
                <PnlStatusStyled status={projection.status} aria-label={tooltip}>
                    <i className={display.icon} />
                    <span>{display.label}</span>
                </PnlStatusStyled>
            }
        />
    )
}
